import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.details.open = false;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}

// MITCH BY AEF - Objectif 11 + Objectif 16 (24/08/2026)
// Conserve exactement la structure validée en français et l'applique aux langues publiées.
const MITCH_DELIVERY_HEADINGS = [
  'LIVRAISON ET PAIEMENT',
  'SHIPPING AND PAYMENT',
  'SHIPPING & PAYMENT',
  'DELIVERY AND PAYMENT',
  'DELIVERY & PAYMENT',
  'ENVIO Y PAGO',
  'ENTREGA Y PAGO',
  'SPEDIZIONE E PAGAMENTO',
  'CONSEGNA E PAGAMENTO',
  'VERSAND UND ZAHLUNG',
  'LIEFERUNG UND ZAHLUNG',
  'VERZENDING EN BETALING',
  'LEVERING EN BETALING',
  'ENVIO E PAGAMENTO',
  'ENTREGA E PAGAMENTO',
  'TESLIMAT VE ODEME',
  'KARGO VE ODEME',
  'الشحن والدفع',
  'التوصيل والدفع'
];

const MITCH_DELIVERY_COPY = {
  fr: {
    aria: 'Livraison et paiement',
    deliveryTitle: 'LIVRAISON STANDARD OFFERTE',
    deliveryDetail: 'France et Europe : 7 à 20 jours ouvrés. Le suivi est communiqué lorsqu’il est disponible.',
    paymentTitle: 'PAIEMENT SSL 100 % SÉCURISÉ',
    paymentDetail: 'Moyens de paiement acceptés : carte bancaire (Visa, Mastercard, American Express), PayPal, Apple Pay, Google Pay, Shop Pay, Revolut, Stripe.',
    flexibleHeading: 'PAIEMENT FLEXIBLE',
    paypalDetail: 'PayPal 4X peut être proposé.',
    klarnaDetail: 'Klarna 3X peut être proposé.',
    fineprint: 'Selon disponibilité, éligibilité et acceptation du prestataire. Les modalités finales s’affichent au paiement.',
    returnTitle: 'RETOUR ET REMBOURSEMENT INTÉGRAL',
    returnDetail: 'Après retour accepté, le remboursement intégral est recrédité sur le moyen de paiement utilisé pour la commande.'
  },
  en: {
    aria: 'Shipping and payment',
    deliveryTitle: 'FREE STANDARD DELIVERY',
    deliveryDetail: 'France and Europe: 7 to 20 business days. Tracking information is provided when available.',
    paymentTitle: '100% SECURE SSL PAYMENT',
    paymentDetail: 'Accepted payment methods: bank card (Visa, Mastercard, American Express), PayPal, Apple Pay, Google Pay, Shop Pay, Revolut, Stripe.',
    flexibleHeading: 'FLEXIBLE PAYMENT',
    paypalDetail: 'PayPal 4X may be offered.',
    klarnaDetail: 'Klarna 3X may be offered.',
    fineprint: 'Subject to availability, eligibility and provider approval. Final terms are shown at checkout.',
    returnTitle: 'RETURNS AND FULL REFUND',
    returnDetail: 'Once the return is accepted, the full refund is credited back to the payment method used for the order.'
  },
  de: {
    aria: 'Lieferung und Zahlung',
    deliveryTitle: 'KOSTENLOSER STANDARDVERSAND',
    deliveryDetail: 'Frankreich und Europa: 7 bis 20 Werktage. Informationen zur Sendungsverfolgung werden bereitgestellt, sobald sie verfügbar sind.',
    paymentTitle: '100 % SICHERE SSL-ZAHLUNG',
    paymentDetail: 'Akzeptierte Zahlungsmethoden: Bankkarte (Visa, Mastercard, American Express), PayPal, Apple Pay, Google Pay, Shop Pay, Revolut, Stripe.',
    flexibleHeading: 'FLEXIBLE ZAHLUNG',
    paypalDetail: 'PayPal 4X kann angeboten werden.',
    klarnaDetail: 'Klarna 3X kann angeboten werden.',
    fineprint: 'Je nach Verfügbarkeit, Berechtigung und Zustimmung des Anbieters. Die endgültigen Bedingungen werden beim Bezahlen angezeigt.',
    returnTitle: 'RÜCKGABE UND VOLLSTÄNDIGE RÜCKERSTATTUNG',
    returnDetail: 'Nach Annahme der Rückgabe wird der vollständige Betrag auf die für die Bestellung verwendete Zahlungsmethode zurückerstattet.'
  },
  es: {
    aria: 'Envío y pago',
    deliveryTitle: 'ENTREGA ESTÁNDAR GRATUITA',
    deliveryDetail: 'Francia y Europa: de 7 a 20 días laborables. La información de seguimiento se comunica cuando está disponible.',
    paymentTitle: 'PAGO SSL 100 % SEGURO',
    paymentDetail: 'Métodos de pago aceptados: tarjeta bancaria (Visa, Mastercard, American Express), PayPal, Apple Pay, Google Pay, Shop Pay, Revolut, Stripe.',
    flexibleHeading: 'PAGO FLEXIBLE',
    paypalDetail: 'PayPal 4X puede estar disponible.',
    klarnaDetail: 'Klarna 3X puede estar disponible.',
    fineprint: 'Sujeto a disponibilidad, elegibilidad y aceptación del proveedor. Las condiciones finales se muestran al pagar.',
    returnTitle: 'DEVOLUCIÓN Y REEMBOLSO ÍNTEGRO',
    returnDetail: 'Una vez aceptada la devolución, el reembolso íntegro se abona en el método de pago utilizado para el pedido.'
  },
  it: {
    aria: 'Spedizione e pagamento',
    deliveryTitle: 'SPEDIZIONE STANDARD GRATUITA',
    deliveryDetail: 'Francia ed Europa: da 7 a 20 giorni lavorativi. Le informazioni di tracciamento vengono comunicate quando disponibili.',
    paymentTitle: 'PAGAMENTO SSL 100% SICURO',
    paymentDetail: 'Metodi di pagamento accettati: carta bancaria (Visa, Mastercard, American Express), PayPal, Apple Pay, Google Pay, Shop Pay, Revolut, Stripe.',
    flexibleHeading: 'PAGAMENTO FLESSIBILE',
    paypalDetail: 'PayPal 4X può essere disponibile.',
    klarnaDetail: 'Klarna 3X può essere disponibile.',
    fineprint: 'In base a disponibilità, idoneità e approvazione del fornitore. Le condizioni finali vengono mostrate al pagamento.',
    returnTitle: 'RESO E RIMBORSO INTEGRALE',
    returnDetail: 'Una volta accettato il reso, il rimborso integrale viene riaccreditato sul metodo di pagamento utilizzato per l’ordine.'
  },
  nl: {
    aria: 'Levering en betaling',
    deliveryTitle: 'GRATIS STANDAARDLEVERING',
    deliveryDetail: 'Frankrijk en Europa: 7 tot 20 werkdagen. Trackinginformatie wordt gedeeld zodra deze beschikbaar is.',
    paymentTitle: '100% VEILIGE SSL-BETALING',
    paymentDetail: 'Geaccepteerde betaalmethoden: bankkaart (Visa, Mastercard, American Express), PayPal, Apple Pay, Google Pay, Shop Pay, Revolut, Stripe.',
    flexibleHeading: 'FLEXIBEL BETALEN',
    paypalDetail: 'PayPal 4X kan worden aangeboden.',
    klarnaDetail: 'Klarna 3X kan worden aangeboden.',
    fineprint: 'Afhankelijk van beschikbaarheid, geschiktheid en goedkeuring door de aanbieder. De definitieve voorwaarden worden bij het afrekenen weergegeven.',
    returnTitle: 'RETOUR EN VOLLEDIGE TERUGBETALING',
    returnDetail: 'Na acceptatie van de retour wordt het volledige bedrag teruggestort op de betaalmethode die voor de bestelling is gebruikt.'
  },
  pt: {
    aria: 'Entrega e pagamento',
    deliveryTitle: 'ENTREGA PADRÃO GRATUITA',
    deliveryDetail: 'França e Europa: 7 a 20 dias úteis. A informação de seguimento é comunicada quando disponível.',
    paymentTitle: 'PAGAMENTO SSL 100% SEGURO',
    paymentDetail: 'Métodos de pagamento aceites: cartão bancário (Visa, Mastercard, American Express), PayPal, Apple Pay, Google Pay, Shop Pay, Revolut, Stripe.',
    flexibleHeading: 'PAGAMENTO FLEXÍVEL',
    paypalDetail: 'PayPal 4X pode estar disponível.',
    klarnaDetail: 'Klarna 3X pode estar disponível.',
    fineprint: 'Sujeito a disponibilidade, elegibilidade e aceitação do prestador. As condições finais são apresentadas no pagamento.',
    returnTitle: 'DEVOLUÇÃO E REEMBOLSO INTEGRAL',
    returnDetail: 'Após a devolução ser aceite, o reembolso integral é creditado no método de pagamento utilizado na encomenda.'
  },
  tr: {
    aria: 'Teslimat ve ödeme',
    deliveryTitle: 'ÜCRETSİZ STANDART TESLİMAT',
    deliveryDetail: 'Fransa ve Avrupa: 7 ila 20 iş günü. Takip bilgileri mevcut olduğunda paylaşılır.',
    paymentTitle: '%100 GÜVENLİ SSL ÖDEME',
    paymentDetail: 'Kabul edilen ödeme yöntemleri: banka/kredi kartı (Visa, Mastercard, American Express), PayPal, Apple Pay, Google Pay, Shop Pay, Revolut, Stripe.',
    flexibleHeading: 'ESNEK ÖDEME',
    paypalDetail: 'PayPal 4X sunulabilir.',
    klarnaDetail: 'Klarna 3X sunulabilir.',
    fineprint: 'Kullanılabilirlik, uygunluk ve sağlayıcı onayına tabidir. Nihai koşullar ödeme sırasında gösterilir.',
    returnTitle: 'İADE VE TAM GERİ ÖDEME',
    returnDetail: 'İade kabul edildikten sonra tutarın tamamı siparişte kullanılan ödeme yöntemine geri yatırılır.'
  },
  ar: {
    aria: 'الشحن والدفع',
    deliveryTitle: 'شحن قياسي مجاني',
    deliveryDetail: 'فرنسا وأوروبا: من 7 إلى 20 يوم عمل. يتم توفير معلومات التتبع عند توفرها.',
    paymentTitle: 'دفع آمن 100٪ عبر SSL',
    paymentDetail: 'وسائل الدفع المقبولة: البطاقة المصرفية (Visa وMastercard وAmerican Express)، PayPal، Apple Pay، Google Pay، Shop Pay، Revolut، Stripe.',
    flexibleHeading: 'دفع مرن',
    paypalDetail: 'قد يتوفر PayPal 4X.',
    klarnaDetail: 'قد يتوفر Klarna 3X.',
    fineprint: 'حسب التوفر والأهلية وموافقة مزود الخدمة. تظهر الشروط النهائية عند الدفع.',
    returnTitle: 'إرجاع واسترداد كامل',
    returnDetail: 'بعد قبول الإرجاع، تتم إعادة المبلغ بالكامل إلى وسيلة الدفع المستخدمة في الطلب.'
  }
};

function normalizeMitchDeliveryLabel(value) {
  let text = (value || '').toString();
  if (text.normalize) text = text.normalize('NFD');

  return text
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

const MITCH_DELIVERY_HEADING_KEYS = MITCH_DELIVERY_HEADINGS.map(normalizeMitchDeliveryLabel);

function isMitchDeliveryHeading(value) {
  const normalized = normalizeMitchDeliveryLabel(value);
  return MITCH_DELIVERY_HEADING_KEYS.some((heading) => normalized === heading || normalized.includes(heading));
}

function getMitchDeliveryLocale() {
  const rawLocale = (
    document.documentElement.lang ||
    (window.Shopify && window.Shopify.locale) ||
    'fr'
  ).toLowerCase();

  if (rawLocale.startsWith('ar')) return 'ar';
  if (rawLocale.startsWith('de')) return 'de';
  if (rawLocale.startsWith('en')) return 'en';
  if (rawLocale.startsWith('es')) return 'es';
  if (rawLocale.startsWith('it')) return 'it';
  if (rawLocale.startsWith('nl')) return 'nl';
  if (rawLocale.startsWith('pt')) return 'pt';
  if (rawLocale.startsWith('tr')) return 'tr';
  return 'fr';
}

function mitchDeliveryIcon(name) {
  const icons = {
    truck: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 6.5h10v10H3zM13 10h4.2l3.3 3.6v2.9H13zM6.5 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM17.5 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/></svg>',
    lock: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="4.5" y="10" width="15" height="10" rx="1.5"/><path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v2.5"/></svg>',
    refund: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 9h18M7 14h4M17.5 13.2a3 3 0 1 1-2.7 4.3M14.8 17.5v-2.7h2.7"/></svg>'
  };

  return icons[name] || '';
}

function ensureMitchDeliveryStyles() {
  if (document.getElementById('mitch-delivery-payment-style')) return;

  const style = document.createElement('style');
  style.id = 'mitch-delivery-payment-style';
  style.textContent = `
    .mitch-delivery-payment {
      width: 100%;
      margin-block: 8px 12px;
      border: 1px solid rgb(246 237 221 / 0.28);
      background: #202219;
      color: #f6eddd;
    }

    .mitch-delivery-payment__table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      background: #202219;
      color: #f6eddd;
      font-family: inherit;
    }

    .mitch-delivery-payment__row > td {
      border-block-end: 1px solid rgb(246 237 221 / 0.18);
      color: #f6eddd;
      vertical-align: top;
    }

    .mitch-delivery-payment__row:last-child > td {
      border-block-end: 0;
    }

    .mitch-delivery-payment__row--priority {
      background: rgb(246 237 221 / 0.055);
    }

    .mitch-delivery-payment__icon {
      width: 48px;
      padding: 15px 9px 15px 14px;
      text-align: center;
    }

    .mitch-delivery-payment__icon svg {
      display: block;
      width: 22px;
      height: 22px;
      margin: 0 auto;
      fill: none;
      stroke: currentColor;
      stroke-width: 1.7;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    .mitch-delivery-payment__copy {
      padding: 13px 14px 13px 4px;
      font-size: 13px;
      line-height: 1.5;
      text-align: start;
    }

    .mitch-delivery-payment__title {
      display: block;
      margin: 0;
      color: #f6eddd;
      font-size: 13px;
      font-weight: 750;
      line-height: 1.35;
      text-transform: uppercase;
    }

    .mitch-delivery-payment__row--priority .mitch-delivery-payment__title {
      font-weight: 800;
      letter-spacing: 0.025em;
    }

    .mitch-delivery-payment__detail {
      display: block;
      margin-block-start: 4px;
      color: rgb(246 237 221 / 0.9);
      font-size: 12.5px;
      line-height: 1.5;
      text-transform: none;
    }

    .mitch-delivery-payment__flex-heading {
      display: block;
      margin-block-start: 11px;
      color: #f6eddd;
      font-size: 11.5px;
      font-weight: 800;
      line-height: 1.35;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    .mitch-delivery-payment__flex-options {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 7px;
      margin-block-start: 7px;
    }

    .mitch-delivery-payment__flex-card {
      min-width: 0;
      padding: 8px 9px;
      border: 1px solid rgb(246 237 221 / 0.14);
      border-radius: 8px;
      background: rgb(246 237 221 / 0.055);
      color: #f6eddd;
    }

    .mitch-delivery-payment__flex-title {
      display: block;
      color: #f6eddd;
      font-size: 11.5px;
      font-weight: 800;
      line-height: 1.35;
      text-transform: none;
    }

    .mitch-delivery-payment__flex-detail {
      display: block;
      margin-block-start: 2px;
      color: rgb(246 237 221 / 0.88);
      font-size: 10.5px;
      line-height: 1.4;
      text-transform: none;
    }

    .mitch-delivery-payment__fineprint {
      display: block;
      margin-block-start: 7px;
      color: rgb(246 237 221 / 0.72);
      font-size: 10px;
      line-height: 1.4;
      text-transform: none;
    }

    @media screen and (max-width: 749px) {
      .mitch-delivery-payment__icon {
        width: 42px;
        padding: 13px 7px 13px 11px;
      }

      .mitch-delivery-payment__icon svg {
        width: 20px;
        height: 20px;
      }

      .mitch-delivery-payment__copy {
        padding: 12px 11px 12px 3px;
        font-size: 12px;
      }

      .mitch-delivery-payment__title {
        font-size: 12px;
      }

      .mitch-delivery-payment__detail {
        font-size: 12px;
      }

      .mitch-delivery-payment__flex-heading {
        font-size: 11px;
      }

      .mitch-delivery-payment__flex-card {
        padding: 7px 8px;
      }

      .mitch-delivery-payment__flex-title {
        font-size: 11px;
      }

      .mitch-delivery-payment__flex-detail {
        font-size: 10px;
      }

      .mitch-delivery-payment__fineprint {
        font-size: 9.5px;
      }
    }

    @media screen and (max-width: 420px) {
      .mitch-delivery-payment__flex-options {
        grid-template-columns: 1fr;
      }
    }
  `;

  document.head.append(style);
}

function formatMitchDeliveryPayment(accordion) {
  if (!(accordion instanceof HTMLElement)) return;

  const details = accordion.querySelector('details');
  const summary = details?.querySelector('.details__header, summary');
  if (!details || !summary) return;
  if (!isMitchDeliveryHeading(summary.textContent)) return;

  const locale = getMitchDeliveryLocale();
  const copy = MITCH_DELIVERY_COPY[locale] || MITCH_DELIVERY_COPY.fr;
  const currentLocale = details.dataset.mitchDeliveryTableLocale;

  if (
    details.dataset.mitchDeliveryTableReady === 'true' &&
    currentLocale === locale &&
    details.querySelector('.mitch-delivery-payment')
  ) {
    return;
  }

  const content = details.querySelector('.details-content');
  if (!content) return;

  ensureMitchDeliveryStyles();

  const wrapper = document.createElement('div');
  wrapper.className = 'mitch-delivery-payment';
  wrapper.innerHTML = `
    <table class="mitch-delivery-payment__table mitch-shipping-table mitch-assurance-table" aria-label="${copy.aria}">
      <tbody>
        <tr class="mitch-delivery-payment__row">
          <td class="mitch-delivery-payment__icon" aria-hidden="true">${mitchDeliveryIcon('truck')}</td>
          <td class="mitch-delivery-payment__copy" dir="auto">
            <strong class="mitch-delivery-payment__title">${copy.deliveryTitle}</strong>
            <span class="mitch-delivery-payment__detail">${copy.deliveryDetail}</span>
          </td>
        </tr>
        <tr class="mitch-delivery-payment__row mitch-delivery-payment__row--priority">
          <td class="mitch-delivery-payment__icon" aria-hidden="true">${mitchDeliveryIcon('lock')}</td>
          <td class="mitch-delivery-payment__copy" dir="auto">
            <strong class="mitch-delivery-payment__title">${copy.paymentTitle}</strong>
            <span class="mitch-delivery-payment__detail">${copy.paymentDetail}</span>
            <span class="mitch-delivery-payment__flex-heading">${copy.flexibleHeading}</span>
            <span class="mitch-delivery-payment__flex-options">
              <span class="mitch-delivery-payment__flex-card">
                <strong class="mitch-delivery-payment__flex-title">PayPal 4X</strong>
                <span class="mitch-delivery-payment__flex-detail">${copy.paypalDetail}</span>
              </span>
              <span class="mitch-delivery-payment__flex-card">
                <strong class="mitch-delivery-payment__flex-title">Klarna 3X</strong>
                <span class="mitch-delivery-payment__flex-detail">${copy.klarnaDetail}</span>
              </span>
            </span>
            <span class="mitch-delivery-payment__fineprint">${copy.fineprint}</span>
          </td>
        </tr>
        <tr class="mitch-delivery-payment__row mitch-delivery-payment__row--priority">
          <td class="mitch-delivery-payment__icon" aria-hidden="true">${mitchDeliveryIcon('refund')}</td>
          <td class="mitch-delivery-payment__copy" dir="auto">
            <strong class="mitch-delivery-payment__title">${copy.returnTitle}</strong>
            <span class="mitch-delivery-payment__detail">${copy.returnDetail}</span>
          </td>
        </tr>
      </tbody>
    </table>
  `;

  content.replaceChildren(wrapper);
  details.classList.add('mitch-shipping-payment-v2');
  details.dataset.mitchShippingPaymentReady = 'true';
  details.dataset.mitchShippingPaymentV2Ready = 'true';
  details.dataset.mitchDeliveryTableReady = 'true';
  details.dataset.mitchDeliveryTableLocale = locale;
}

function formatAllMitchDeliveryPayments() {
  if (!window.location.pathname.includes('/products/')) return;
  document.querySelectorAll('accordion-custom').forEach(formatMitchDeliveryPayment);
}

formatAllMitchDeliveryPayments();

if (!window.__mitchDeliveryPaymentObserver && document.body) {
  window.__mitchDeliveryPaymentObserver = new MutationObserver(() => {
    formatAllMitchDeliveryPayments();
  });

  window.__mitchDeliveryPaymentObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
}

// MITCH BY AEF - Objectif 16 (24/08/2026) - Guide des tailles multilingue
// Le contenu traduit reste intact : seule la même structure visuelle que le français est reconstruite.
const MITCH_SIZE_GUIDE_HEADINGS = [
  'guide des tailles',
  'guide de tailles',
  'size guide',
  'sizing guide',
  'guia de tallas',
  'guía de tallas',
  'tabla de tallas',
  'guida alle taglie',
  'tabella taglie',
  'größentabelle',
  'grössentabelle',
  'groessentabelle',
  'größenratgeber',
  'grössenratgeber',
  'groessenratgeber',
  'grossenratgeber',
  'größenguide',
  'groessenguide',
  'maattabel',
  'maatgids',
  'guia de tamanhos',
  'guia de tamanho',
  'tabela de tamanhos',
  'beden rehberi',
  'beden tablosu',
  'ölçü rehberi',
  'ölçü tablosu',
  'دليل المقاسات',
  'جدول المقاسات'
];

function normalizeMitchSizeGuideLabel(value) {
  let text = (value || '').toString().toLowerCase();
  if (text.normalize) text = text.normalize('NFD');
  return text
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const MITCH_SIZE_GUIDE_HEADING_KEYS = MITCH_SIZE_GUIDE_HEADINGS.map(normalizeMitchSizeGuideLabel);

function isMitchSizeGuideHeading(value) {
  const heading = normalizeMitchSizeGuideLabel(value);
  return MITCH_SIZE_GUIDE_HEADING_KEYS.some((label) => heading === label || heading.includes(label));
}

function cleanMitchSizeGuideValue(value) {
  return (value || '')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .trim();
}

function splitMitchSizeGuideSegments(line) {
  return line
    .split(/\s+(?:-|–|—|·|•|→)\s+/)
    .map(cleanMitchSizeGuideValue)
    .filter(Boolean);
}

function isMitchSizeGuideRow(line) {
  const value = normalizeMitchSizeGuideLabel(cleanMitchSizeGuideValue(line));
  const sizePrefix = '(?:(?:taille|size|große|grosse|groesse|talla|taglia|maat|tamanho|beden)\\s+)?';
  const alphaSize = '(?:xxxs|xxs|xs|s|m|l|xl|xxl|xxxl|2xl|3xl|4xl|5xl)';
  const alphaPattern = new RegExp('^' + sizePrefix + alphaSize + '\\b', 'i');

  if (alphaPattern.test(value)) return true;
  if (/^(?:taille unique|taille universelle|one size|unique size|einheitsgroße|einheitsgrosse|einheitsgroesse|talla unica|taglia unica|maat universeel|tamanho unico|tek beden|مقاس موحد)\b/i.test(value)) return true;
  if (/^(?:eu|fr|us|uk)\s*\d{2,3}\b/i.test(value)) return true;
  return /^\d{2,3}\b/.test(value);
}

function buildMitchSizeGuideRow(line, index, lines) {
  const nextLine = lines[index + 1] || '';
  const parts = splitMitchSizeGuideSegments(line);

  if (isMitchSizeGuideRow(line) && parts.length > 1) {
    return { type: 'size', parts };
  }

  if (line.length <= 36 && !/[:：]/.test(line) && isMitchSizeGuideRow(nextLine)) {
    return { type: 'section', parts: [line] };
  }

  const colonMatches = line.match(/[:：]/g);
  if (colonMatches && colonMatches.length === 1) {
    const colonIndex = line.search(/[:：]/);
    const label = cleanMitchSizeGuideValue(line.slice(0, colonIndex));
    const value = cleanMitchSizeGuideValue(line.slice(colonIndex + 1));
    if (label && value) return { type: 'pair', parts: [label, value] };
  }

  if (isMitchSizeGuideRow(line)) {
    return { type: 'size', parts: [line] };
  }

  return { type: 'note', parts: [line] };
}

function appendMitchSizeGuideCell(row, tag, text, className, span) {
  const cell = document.createElement(tag);
  cell.className = className;
  cell.setAttribute('dir', 'auto');
  if (tag === 'th') cell.setAttribute('scope', 'row');
  if (span && span > 1) cell.colSpan = span;
  cell.textContent = text;
  row.appendChild(cell);
  return cell;
}

function styleExistingMitchSizeGuideTable(details, content) {
  const existing = content.querySelector('table');
  if (!existing || existing.classList.contains('mitch-assurance-table')) return false;

  details.classList.add('mitch-size-guide-v2');
  existing.classList.add('mitch-size-table');

  if (!existing.parentElement?.classList.contains('mitch-size-table-wrap')) {
    const wrapper = document.createElement('div');
    wrapper.className = 'mitch-size-table-wrap';
    existing.parentNode?.insertBefore(wrapper, existing);
    wrapper.appendChild(existing);
  }

  details.dataset.mitchSizeGuideReady = 'true';
  return true;
}

function formatMitchTranslatedSizeGuide(details) {
  if (!(details instanceof HTMLDetailsElement)) return;

  const summary = details.querySelector('.details__header, summary');
  if (!summary || !isMitchSizeGuideHeading(summary.textContent)) return;

  const content = details.querySelector('.details-content');
  if (!content) return;

  if (details.dataset.mitchSizeGuideReady === 'true' && content.querySelector('.mitch-size-table')) return;
  if (styleExistingMitchSizeGuideTable(details, content)) return;

  const rawText = content.innerText || content.textContent || '';
  const lines = rawText
    .replace(/\r/g, '')
    .split(/\n+/)
    .map(cleanMitchSizeGuideValue)
    .filter(Boolean);

  if (!lines.length) return;

  const rows = lines.map((line, index) => buildMitchSizeGuideRow(line, index, lines));
  const maxColumns = rows.reduce((max, row) => {
    if (row.type === 'size') return Math.max(max, row.parts.length);
    if (row.type === 'pair') return Math.max(max, 2);
    return max;
  }, 1);

  const wrapper = document.createElement('div');
  wrapper.className = 'mitch-size-table-wrap';

  const table = document.createElement('table');
  table.className = `mitch-size-table mitch-size-table--cols-${Math.min(maxColumns, 7)}`;
  table.setAttribute('aria-label', cleanMitchSizeGuideValue(summary.textContent || 'Guide des tailles'));

  const tbody = document.createElement('tbody');

  rows.forEach((row) => {
    const tr = document.createElement('tr');
    tr.className = `mitch-size-table__row mitch-size-table__row--${row.type}`;

    if (row.type === 'size') {
      row.parts.forEach((part, partIndex) => {
        const remaining = maxColumns - row.parts.length + 1;
        const span = partIndex === row.parts.length - 1 ? remaining : 1;
        appendMitchSizeGuideCell(
          tr,
          partIndex === 0 ? 'th' : 'td',
          part,
          partIndex === 0 ? 'mitch-size-table__size' : 'mitch-size-table__measure',
          span
        );
      });
    } else if (row.type === 'pair') {
      appendMitchSizeGuideCell(tr, 'th', row.parts[0], 'mitch-size-table__label', 1);
      appendMitchSizeGuideCell(tr, 'td', row.parts[1], 'mitch-size-table__value', Math.max(1, maxColumns - 1));
    } else if (row.type === 'section') {
      appendMitchSizeGuideCell(tr, 'th', row.parts[0], 'mitch-size-table__section', maxColumns);
    } else {
      appendMitchSizeGuideCell(tr, 'td', row.parts[0], 'mitch-size-table__note', maxColumns);
    }

    tbody.appendChild(tr);
  });

  table.appendChild(tbody);
  wrapper.appendChild(table);
  content.replaceChildren(wrapper);
  details.classList.add('mitch-size-guide-v2');
  details.dataset.mitchSizeGuideReady = 'true';
}

function formatAllMitchTranslatedSizeGuides() {
  if (!window.location.pathname.includes('/products/')) return;
  const productRoot = document.querySelector('[data-testid="product-information"]') || document;
  productRoot.querySelectorAll('details').forEach(formatMitchTranslatedSizeGuide);
}

formatAllMitchTranslatedSizeGuides();

document.addEventListener('DOMContentLoaded', formatAllMitchTranslatedSizeGuides, { once: true });

if (!window.__mitchTranslatedSizeGuideObserver && document.body) {
  window.__mitchTranslatedSizeGuideObserver = new MutationObserver(() => {
    formatAllMitchTranslatedSizeGuides();
  });

  window.__mitchTranslatedSizeGuideObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
}
